import { indexdDbWrapper } from './indexdDB';
import { isIOS } from '@/tools/helpers';
import { logger } from '@/tools';
import { throttle } from './throttle';
import { now } from './syncronizeServerTime';
import { mutationTypes } from '@/types';

// ##################
const throttleTimeLocalSeekCorrection = 500; // trottle timeupdate events that check if local time is desired time
const localMaxSyncDifferenceInit = 0.1; // in seconds; max difference of media current time to desired time
const localMaxSyncDifferenceStep = 0.05; // in seconds; max difference of media current time to desired time
let localMaxSyncDifference = localMaxSyncDifferenceInit; // in seconds; max difference of media current time to desired time
const maxLocalSyncAttempts = 30;
const fastSyncPlayback = 1.05; // Seek to Correct time playback value if the playback is close to the desired location
const slowSyncPlayback = 0.95; // 25ms accuracy on every 500ms call worst case its 750ms between 2 calls(timeupdate gets called every 15-250ms) -> 37.5ms difference at worst between calls
// ##################

let commit = null;
let $media = null;
let progressFunction = null;
let isAudioDataLoaded = false;
let audioDataLoading = false;
let startTime = 2; // in seconds
let configStartTimeTimeCorrected = 2; // in seconds
let currentStartTime = 0; // in seconds
let mediaDuration = 0;
let videoStartTimeout = null;
let countDownInterval = null;
let startOffset = null;
let isLoop = false;
let delayBetweenLoop = 1; // in seconds
let initialized = false;
let isLoading = false;
let audioEnded = false;
let cacheMedia = true;
let localSync = false;
let startTimeFunction = null;
let serverTimeOffset = 0;
let startTimeWasUsed = false;
let serverStartTimeInterval = null;
let isSync = false;
let syncStartTime = 0;
const secondsUntilForcedSync = 5;
const useIndexDb = false;
const createObjectURL = window.webkitURL ? window.webkitURL.createObjectURL : URL.createObjectURL;

// return Date.now in seconds
function roundToMs(num) {
	return Math.round(num * 1000) / 1000;
}

// function median(values) {
// 	values.sort((a, b) => a - b);
// 	const half = Math.floor(values.length / 2);loadAnd
// 	if (values.length % 2) return values[half];
// 	return (values[half - 1] + values[half]) / 2.0;
// }

let mediaQueued = false;
function playMediaAt(_startTime) {
	logger.log('playMediaAt _startTime', _startTime, 'mediaQueued', mediaQueued);
	if (mediaQueued) return;
	mediaQueued = true;
	// start time in s
	currentStartTime = _startTime;
	if (countDownInterval) clearInterval(countDownInterval);
	if (videoStartTimeout) clearTimeout(videoStartTimeout);
	const timeToStartDiff = _startTime - now();
	if (timeToStartDiff > 0) {
		logger.log('will start in the future');
		showMessage('Will start playback soon.');
		countDownInterval = setInterval(() => {
			const startDiff = _startTime - now();
			if (startDiff < 0) {
				playMedia();
				clearInterval(countDownInterval);
			}
			progressFunction(100 - (startDiff / timeToStartDiff) * 100);
		}, 1000);
		videoStartTimeout = setTimeout(async () => {
			playMedia();
		}, (_startTime - now()) * 1000);
	} else {
		logger.log('has already ended');
		const timeBeforeEnd = _startTime + mediaDuration - now();
		if (timeBeforeEnd > 0) {
			startOffset = roundToMs(now() - _startTime); // Start now with x second
			logger.log(`Start now with ${startOffset} second`);
			playMedia();
		} else {
			logger.log('calculate next start time');
			if (isLoop) {
				mediaQueued = false;
				getNextStartTime();
			}
		}
	}
}

function getNextStartTime() {
	const multiplier = Math.floor((now() - configStartTimeTimeCorrected) / (mediaDuration + delayBetweenLoop));
	logger.log('multiplier', multiplier);
	if (serverStartTimeInterval) clearInterval(serverStartTimeInterval);
	if (multiplier > 0) {
		serverStartTimeInterval = setInterval(async () => {
			let serverStartTime = parseInt(await startTimeFunction(), 10) / 1000;
			if (serverStartTime != startTime || startTimeWasUsed) {
				logger.log('serverStart different: ', serverStartTime, startTime);
				startTime = serverStartTime;
				configStartTimeTimeCorrected = serverStartTime;
				if (serverTimeOffset) configStartTimeTimeCorrected += serverTimeOffset;
				clearInterval(serverStartTimeInterval);
				startTimeWasUsed = false;
				playMediaAt(configStartTimeTimeCorrected);
			}
		}, 1000);
	} else {
		startTimeWasUsed = true;
		playMediaAt(configStartTimeTimeCorrected);
	}
}

function playMedia() {
	logger.log('playMedia');
	$media.play();
	mediaQueued = false;
}

async function loadAudio(src) {
	if (useIndexDb && indexdDbWrapper.isAvailable()) {
		await indexdDbWrapper.init();
		const data = await indexdDbWrapper.read(src);
		if (data === undefined) {
			return preloadAudio(src);
		}
		logger.log('loaded from cache');
		progressFunction(100);
		isAudioDataLoaded = true;
		logger.log('set obj url');
		$media.src = createObjectURL(data);
		return undefined;
	}
	return preloadAudio(src);
}

// on older Safari Versions on iPad BlobURLs are not supported and the audio has to be downloaded every time
function isIpadOnSafari() {
	let isIpad =
		window.navigator.userAgent.toLowerCase().includes('macintosh') ||
		window.navigator.userAgent.toLowerCase().includes('ipad');
	let isSafari = window.navigator.userAgent.toLowerCase().includes('safari');
	return isIpad && isSafari;
}

function preloadAudio(src) {
	logger.log('mediaSyncronization preloadAudio', src);
	return new Promise((resolve, reject) => {
		if (audioDataLoading) {
			resolve();
			return;
		}

		audioDataLoading = true;
		const xhr = new XMLHttpRequest();
		xhr.open('GET', src, true);
		// xhr.withCredentials = true;
		xhr.responseType = 'blob';
		xhr.onprogress = event => {
			if (event.lengthComputable) {
				progressFunction(Math.floor((event.loaded / event.total) * 100));
			}
		};
		xhr.onload = () => {
			logger.log('preloadAudio: ', xhr.status, src);
			if (xhr.status === 200) {
				isAudioDataLoaded = true;
				logger.log('mediaSyncronization audioDataLoaded', isAudioDataLoaded);
				if (useIndexDb && indexdDbWrapper.isAvailable() && !isIpadOnSafari()) {
					indexdDbWrapper.init().then(() => {
						logger.log('write to indexdDB');
						indexdDbWrapper.write(src, xhr.response);
					});
				}
				logger.log('set obj url2');
				$media.src = createObjectURL(xhr.response);
			}
		};
		xhr.onloadend = () => {
			audioDataLoading = false;
			resolve();
		};
		xhr.onerror = error => {
			isAudioDataLoaded = false;
			reject(new Error(`Error downloading audio data ${error.err}`));
		};
		xhr.send();
	});
}

function onEnded() {
	logger.log('onEnded isLoop audioEnded', isLoop, audioEnded);
	lastError = null;
	testCount = 0;
	localSyncErrors = 0;
	accelDeccel = false;
	mediaQueued = false;
	syncStartTime = 0;
	isAudioDataLoaded = false;
}

// If the audio is paused start it immediately again.
// This prevents stopping the audio when the headphone jack is unplugged
function onPause() {
	logger.log('something paused the media playback.');
	lastError = null;
	testCount = 0;
	localSyncErrors = 0;
	accelDeccel = false;
	syncStartTime = 0;
	commit(mutationTypes.audioStop);
	// if (audioEnded || localSync) return;
	// playMedia()
}

function onPlay() {
	commit(mutationTypes.audioStart, {});
	isSync = false;
	syncStartTime = now();
}

export async function loadAndPlay({ _serverTimeOffset, srcUrl }) {
	if (isAudioDataLoaded && $media.currentTime > 0 && !$media.ended) {
		$media.currentTime = 0;
		$media.pause();
		return;
	}

	serverTimeOffset = _serverTimeOffset;
	let checkStartTime = now() - (startTime + mediaDuration);
	logger.log('checkStuff', checkStartTime);
	if (now() - (startTime + mediaDuration) > 0 || startTime < 0) {
		let newStartTime = await startTimeFunction();
		let newStartTimeInt = parseInt(newStartTime, 10) / 1000;
		logger.log('newStartTime', newStartTimeInt, startTime);
		if (newStartTimeInt > startTime) {
			logger.log('found new startTime', newStartTime, startTime);
			startTime = newStartTimeInt;
		} else {
			logger.log('Old starttime, try fetch in 1s');
			setTimeout(() => {
				if (isLoading) return; // If something gets loaded that means no need to fetch a new starttime anymore
				loadAndPlay({ _serverTimeOffset, srcUrl });
			}, 1000);
			return;
		}
	}

	logger.log('loadAndPlay');
	localSyncErrors = 0;
	// Reset local max sync difference, because we may have more luck this time.
	localMaxSyncDifference = localMaxSyncDifferenceInit;
	testCount = 0;
	configStartTimeTimeCorrected = serverTimeOffset + startTime;
	if (isLoading) {
		logger.warn('mediaSyncronization already loading media.');
		return;
	}
	logger.log('cacheMedia', cacheMedia);
	if (cacheMedia && createObjectURL) {
		try {
			isLoading = true;
			await loadAudio(srcUrl);
			isLoading = false;
		} catch (error) {
			isLoading = false;
			logger.error('Loading media failed:', error);
			showMessage('Das Laden der Audiodatei hat nicht funktioniert. Bitte versuche es erneut.');
			return;
		}
		if (isAudioDataLoaded) {
			playMediaAt(configStartTimeTimeCorrected);
		}
	} else {
		playMediaAt(configStartTimeTimeCorrected);
	}
}

function showMessage(message) {
	logger.log(message);
}

function setCurrentTime(diff) {
	if (isIOS) {
		localSync = true;
		$media.currentTime += diff;
		// $media.play().then(() => {
		// 	logger.log('started playing')
		// });
		localSync = false;
	} else {
		$media.currentTime += diff;
	}
}

let lastError = null;
let testCount = 0;
let localSyncErrors = 0;
let accelDeccel = false;
function seekToCorrectTime() {
	if ($media.paused || !isAudioDataLoaded) return;
	if (accelDeccel) {
		$media.playbackRate = 1.0;
		accelDeccel = false;
	}
	// Initial sync to offset if the media started already.
	if (startOffset) {
		$media.currentTime = startOffset;
		startOffset = null;
		return;
	}

	const elapsedSinceSyncStart = now() - syncStartTime;

	const diff = roundToMs(now() - currentStartTime - $media.currentTime);
	logger.log('cur, sync: ', $media.currentTime, elapsedSinceSyncStart);
	// const expectedTime = roundToMs($media.currentTime + diff);
	// if (expectedTime < 0) {
	// 	logger.log("$media.currentTime - diff", $media.currentTime - diff);
	// 	$media.currentTime = 0;
	// 	return;
	// } else if (expectedTime > mediaDuration) {
	// 	logger.warn("$media.currentTime + diff", $media.currentTime + diff);
	// 	return;
	// }
	// Time correct if more than 10 ms off
	// logger.log("lastError", lastError);
	if (
		(Math.abs(diff) > localMaxSyncDifference || diff < 0) &&
		!isSync &&
		!(elapsedSinceSyncStart > secondsUntilForcedSync)
	) {
		if (localSync) logger.warn('time correction but localSync');
		$media.muted = true;
		logger.log('diff ', diff, ' currentTime ', $media.currentTime, ' - ', now() - currentStartTime);
		if (lastError === null) {
			setCurrentTime(diff);
			localSync = false;
			lastError = diff;
		} else {
			const cumulatedDiff = diff + (diff + lastError) / 2;
			if (Math.abs(diff) < localMaxSyncDifference * 2) {
				if (diff > 0) $media.playbackRate = fastSyncPlayback;
				if (diff < 0) $media.playbackRate = slowSyncPlayback;
				accelDeccel = true;
			} else {
				setCurrentTime(cumulatedDiff);
			}
			lastError = cumulatedDiff;
			if (++localSyncErrors > maxLocalSyncAttempts) {
				// If there are too many errors, increase the sync differnece
				localSyncErrors = 0;
				localMaxSyncDifference = roundToMs(localMaxSyncDifference + localMaxSyncDifferenceStep);
				logger.log('localMaxSyncDifference', localMaxSyncDifference);
			}
		}
	} else {
		if (++testCount < 5) {
			logger.log('XXXX ', diff, ' currentTime ', $media.currentTime, ' - ', now() - currentStartTime);
		} else {
			isSync = true;
		}
		$media.muted = false;
	}
}

export function onClickTogglePlayMedia() {
	// Play and pause to circumvent playback prevention on mobile devices.
	if (!initialized) {
		const playPromise = $media.play();
		initialized = true;
		if (playPromise) {
			playPromise
				.then(() => {
					logger.log('initialized pause');
					$media.pause();
				})
				.catch(error => {
					logger.warn('onClickTogglePlayMedia playPromise failed', error);
				});
		} else {
			setTimeout(() => {
				logger.log('timeout pause');
				$media.pause();
			}, 10);
		}
	}
	// Pause playback if currently playing.
	else if ($media && !$media.paused) {
		$media.pause();
		showMessage('Press play to resynchronize and start playing.');
		return;
	}
}

function onCanPlay() {
	isAudioDataLoaded = true;
}

const throttledSeekToCorrectTime = throttle(seekToCorrectTime, throttleTimeLocalSeekCorrection);

export function initializeMediaSyncronization(options) {
	({ $media, startTime, progressFunction, startTimeFunction, commit } = options);
	logger.error('startTime', startTime);
	cacheMedia = options.cacheMedia !== undefined ? options.cacheMedia : true;
	// cacheMedia = false; // FIXME for now only cached works since seek will always jump to currentTime 0 if not cached
	$media.addEventListener('timeupdate', throttledSeekToCorrectTime);
	$media.addEventListener('ended', onEnded);
	$media.addEventListener('pause', onPause);
	$media.addEventListener('play', onPlay);
	// If seeking to time starts before canplay is fired the media resets to the start and will not play.
	if (!cacheMedia) $media.addEventListener('canplay', onCanPlay);
	({ mediaDuration, isLoop = true, delayBetweenLoop = 1 } = options);
	mediaDuration = roundToMs(mediaDuration); // round to millisecond precision
}

export function unSync() {
	logger.log('mediaSyncronization unSync');
	$media.removeEventListener('timeupdate', throttledSeekToCorrectTime);
	$media.removeEventListener('ended', onEnded);
	$media.removeEventListener('pause', onPause);
	$media.removeEventListener('canplaythrough', onCanPlay);
	$media.removeEventListener('play', onPlay);
}
